import axios from "axios";
import { buildAuthMeCall, AUTH_ME_TIMEOUT_MS, AUTH_ME_RETRY_BACKOFF_MS, AUTH_ME_RETRY_TIMEOUT_MS } from "./authMeCall";

// Runtime-resolved baseURL (May 27 2026 hotfix).
//
// Previously: `const BACKEND = process.env.REACT_APP_BACKEND_URL` which
// inlines a build-time value into the JS bundle. On Emergent's custom-
// domain deployments, the build process injected the platform's INTERNAL
// hostname (e.g., `https://astral-ground.emergent.host`) instead of the
// user-facing domain (`https://vedicbhrigu.com`). This made every axios
// call from the production page (vedicbhrigu.com) into a cross-origin
// request against the platform host — which broke the self-hosted Google
// OAuth flow because the state cookie set on vedicbhrigu.com was never
// sent on the cross-origin POST to astral-ground.emergent.host.
//
// Fix: compute baseURL from the live page origin at runtime. API calls
// always go to the page's own /api/* path, so they are always same-origin
// → cookies always sent → OAuth state validation always succeeds. Both
// preview and prod serve /api/* on the same origin, so this works for
// every environment (verified by hitting both /api/health endpoints).
//
// Build-time env var is used only as a non-browser fallback (SSR, tests).
const BACKEND = (typeof window !== "undefined" && window.location?.origin)
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || "");
export const API = `${BACKEND}/api`;

const api = axios.create({ baseURL: API, timeout: 15000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("stellar_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Region-detection signals (Feb 2026 — CF headers not forwarded from
  // edge to origin in Emergent's current infra, so we help the backend
  // detect country/currency client-side).
  //
  // X-Client-Timezone: browser-derived, ~100% reliable for non-VPN users.
  //   Backend maps common India timezones (Asia/Kolkata, Asia/Calcutta)
  //   to country=IN when Cloudflare headers are missing.
  //
  // X-Preferred-Currency: hard user override from the currency toggle on
  //   /pricing. Persisted in localStorage. Takes precedence over both
  //   timezone and any CF header — respects explicit user intent.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) config.headers["X-Client-Timezone"] = tz;
  } catch (_e) { /* older browsers — leave header off */ }

  try {
    const pref = localStorage.getItem("preferredCurrency");
    if (pref === "INR" || pref === "USD") {
      config.headers["X-Preferred-Currency"] = pref;
    }
  } catch (_e) { /* localStorage disabled — leave header off */ }

  return config;
});

// ─────────────────────────────────────────────────────────────────────
// Global 401 response interceptor (Jul 28 2026)
// ─────────────────────────────────────────────────────────────────────
// Before this: any API call that returned 401 dumped a raw "Not
// authenticated" toast/error into the surface it was called from,
// stranding users on a page that couldn't recover — the app-wide
// dead-end recurrence flagged in the handoff notes.
//
// After this: a single choke point wipes the token, tells the user
// their session ended, and redirects to /login?next=<current> so the
// user resumes exactly where they were once they sign back in.
//
// Guards:
//   · Skip auth-adjacent pages so a wrong-password 401 on /login doesn't
//     trigger a self-redirect loop or an alarming "session ended" toast.
//   · Skip 401s from the auth endpoints themselves (/auth/login, /auth/
//     signup, /auth/google) — those are expected wrong-credentials
//     responses handled locally by the calling component.
//   · Idempotency: `_redirecting` flag prevents multi-fire when several
//     parallel API calls all 401 at once (e.g. dashboard load after
//     token expiry) — otherwise we'd stack toasts and thrash the URL.
//   · Toast text differs by state: had-a-token → "session ended";
//     no-token → silent (user was never signed in; just gate them in).
const AUTH_ADJACENT_PATHS = [
  "/login", "/register", "/signup",
  "/forgot-password", "/reset-password",
  "/auth/google", "/auth/callback",
];
const AUTH_ADJACENT_ENDPOINTS = [
  "/auth/login", "/auth/signup", "/auth/register",
  "/auth/google", "/auth/reset-password", "/auth/forgot-password",
];
let _redirecting = false;

const isAuthAdjacentPath = (pathname) =>
  AUTH_ADJACENT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const isAuthAdjacentEndpoint = (url) => {
  if (!url) return false;
  const u = url.startsWith("http") ? new URL(url).pathname : url;
  return AUTH_ADJACENT_ENDPOINTS.some((e) => u.includes(e));
};

api.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    if (err?.response?.status !== 401) return Promise.reject(err);
    if (typeof window === "undefined") return Promise.reject(err);

    const reqUrl = err.config?.url || "";
    if (isAuthAdjacentEndpoint(reqUrl)) return Promise.reject(err);

    const path = window.location.pathname || "/";
    if (isAuthAdjacentPath(path)) return Promise.reject(err);

    if (_redirecting) return Promise.reject(err);
    _redirecting = true;

    const hadToken = !!localStorage.getItem("stellar_token");
    try { localStorage.removeItem("stellar_token"); } catch { /* swallow */ }

    const next = `${window.location.pathname}${window.location.search}${window.location.hash}` || "/app";
    const params = new URLSearchParams();
    params.set("next", next);
    // Login.jsx reads `reason` on mount and surfaces the appropriate
    // toast. Passing via URL (instead of firing toast.error() here) is
    // the only reliable way — the window.location.replace() below tears
    // down the current React tree before sonner can render the toast.
    if (hadToken) params.set("reason", "session_ended");
    const target = `/login?${params.toString()}`;
    // `replace` (not `href`) so the un-authed URL doesn't sit in the
    // browser back stack — otherwise "back" from /login lands on the
    // exact page that 401'd and loops the whole thing.
    window.location.replace(target);
    return Promise.reject(err);
  },
);

// Safely turn a FastAPI/axios error response into a string.
// FastAPI 422 validation errors come back as `{detail: [{type, loc, msg, input, url}]}`,
// which when rendered as a React child throws "Objects are not valid as a React child".
export const errorMessage = (err, fallback = "Something went wrong") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    // Pydantic v2 validation errors — surface the first .msg
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first?.msg) return first.msg;
  }
  if (detail && typeof detail === "object" && detail.msg) return detail.msg;
  return fallback;
};

// ─────────────────────────────────────────────────────────────────────
// Auth-me hardened caller (Jul 3 2026 — mobile-data resilience fix)
// ─────────────────────────────────────────────────────────────────────
// The global 15s axios timeout is too tight for Indian 4G/5G where /auth/me
// legitimately takes 8-14s under packet loss. Combined with the 15s
// AuthHealthMonitor watchdog, the two timers raced and produced false
// "Session expired" logouts for users whose tokens were perfectly valid.
//
// The pure retry logic lives in ./authMeCall.js (so tests can import it
// without pulling axios's ESM entry through jest's babel transformer).
// Here we wire the real axios client into it.
//
// Timing invariant (pinned by test_auth_watchdog_invariant.py):
//   AUTH_ME_TIMEOUT_MS + AUTH_ME_RETRY_BACKOFF_MS + AUTH_ME_RETRY_TIMEOUT_MS
//     < WATCHDOG_MS  (in AuthHealthMonitor.jsx)
// so the watchdog can only fire on a genuinely stuck app, never on a
// slow-but-working /auth/me round-trip.
export { AUTH_ME_TIMEOUT_MS, AUTH_ME_RETRY_BACKOFF_MS, AUTH_ME_RETRY_TIMEOUT_MS };

export const authMeCall = buildAuthMeCall(
  (options) => api.get("/auth/me", options),
);

export default api;
